import React, { useState, useRef, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaFilter } from 'react-icons/fa';
import CasefileForm from '../casefiledetails/CasefileForm';
import AccessRequestsPanel from '../requests/AccessRequestsPanel';
import GrantedAccessList from '../requests/GrantedAccessList';

const YourCasesTab = ({ user, casefiles, allEvidence }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterBy, setFilterBy] = useState('all');
  const filterPopupRef = useRef(null);
  const filterButtonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterPopupRef.current &&
        !filterPopupRef.current.contains(event.target) &&
        filterButtonRef.current &&
        !filterButtonRef.current.contains(event.target)
      ) {
        setIsFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const sortedAndFilteredCasefiles = useMemo(() => {
    return casefiles
      .filter((file) => {
        if (filterBy === 'public') return file.isPublic;
        if (filterBy === 'private') return !file.isPublic;
        return true;
      })
      .sort((a, b) => {
        const valA = sortBy === 'title' ? a.title.toLowerCase() : a.createdAt?.toDate();
        const valB = sortBy === 'title' ? b.title.toLowerCase() : b.createdAt?.toDate();
        
        if (!valA || !valB) return 0;

        if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
        if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
  }, [casefiles, sortBy, sortDirection, filterBy]);

  const handleFilterSelect = (type, value) => {
    if (type === 'sort') setSortBy(value);
    if (type === 'direction') setSortDirection(value);
    if (type === 'filter') setFilterBy(value);
    setIsFilterOpen(false);
  };

  return (
    <div className="tab-content your-cases-tab">
      <div className="tab-header">
        <h2>Your Casefiles</h2>
        <button 
          ref={filterButtonRef} 
          className="filter-btn" 
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <FaFilter />
          <span>Sort / Filter</span>
        </button>
      </div>

      {isFilterOpen && (
        <div ref={filterPopupRef} className="filter-popup">
          <div className="filter-section">
            <div className="filter-section-title">Sort By</div>
            <div className="filter-options">
              <button 
                onClick={() => handleFilterSelect('sort', 'createdAt')} 
                className={`filter-option ${sortBy === 'createdAt' ? 'active' : ''}`}
              >
                Date Created
              </button>
              <button 
                onClick={() => handleFilterSelect('sort', 'title')} 
                className={`filter-option ${sortBy === 'title' ? 'active' : ''}`}
              >
                A-Z
              </button>
            </div>
          </div>
          <div className="filter-section">
            <div className="filter-section-title">Direction</div>
            <div className="filter-options">
              <button 
                onClick={() => handleFilterSelect('direction', 'desc')} 
                className={`filter-option ${sortDirection === 'desc' ? 'active' : ''}`}
              >
                Descending
              </button>
              <button 
                onClick={() => handleFilterSelect('direction', 'asc')} 
                className={`filter-option ${sortDirection === 'asc' ? 'active' : ''}`}
              >
                Ascending
              </button>
            </div>
          </div>
          <div className="filter-section">
            <div className="filter-section-title">Visibility</div>
            <div className="filter-options">
              <button 
                onClick={() => handleFilterSelect('filter', 'all')} 
                className={`filter-option ${filterBy === 'all' ? 'active' : ''}`}
              >
                All Cases
              </button>
              <button 
                onClick={() => handleFilterSelect('filter', 'public')} 
                className={`filter-option ${filterBy === 'public' ? 'active' : ''}`}
              >
                Public Only
              </button>
              <button 
                onClick={() => handleFilterSelect('filter', 'private')} 
                className={`filter-option ${filterBy === 'private' ? 'active' : ''}`}
              >
                Private Only
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="casefiles-content">
        {casefiles.length === 0 ? (
          <div className="empty-state">
            <p>You haven't created any casefiles yet.</p>
            <p>Get started by creating your first case!</p>
          </div>
        ) : sortedAndFilteredCasefiles.length === 0 ? (
          <div className="empty-state">
            <p>No casefiles match your current filters.</p>
            <button onClick={() => handleFilterSelect('filter', 'all')} className="reset-filters-btn">
              Show All Cases
            </button>
          </div>
        ) : (
          <ul className="casefiles-list">
            {sortedAndFilteredCasefiles.map((file) => (
              <li key={file.id} className={`casefile-item ${file.isPublic ? 'casefile-item-public' : ''}`} data-casefile-id={file.id}>
                {file.isPublic && (
                  <div className="public-indicator">
                    <span className="public-text">PUBLIC</span>
                  </div>
                )}
                <div className="casefile-content">
                  <Link to={`/casefile/${file.id}`} className="casefile-title">
                    {file.title}
                  </Link>
                  <p className="casefile-date">
                    Created: {file.createdAt ? new Date(file.createdAt.toDate()).toLocaleString() : 'N/A'}
                  </p>
                  {(() => {
                    const evidenceCount = allEvidence.filter(e => e.casefileId === file.id).length;
                    return evidenceCount > 0 && (
                      <p className="evidence-count">
                        📹 {evidenceCount} evidence file{evidenceCount !== 1 ? 's' : ''}
                      </p>
                    );
                  })()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Access Requests Panel */}
      <AccessRequestsPanel user={user} />

      {/* Granted Access List */}
      <GrantedAccessList user={user} />

      {/* Case Creation Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <CasefileForm user={user} onClose={() => setIsModalOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
};

export default YourCasesTab; 